import type { CalcSnapshot, LedgerSnapshotRow } from './calcSnapshot';
import type { CanonicalManifest, ManifestValue } from './canonicalManifest';
import { buildCanonicalManifest, valuesMatch } from './canonicalManifest';

// Why a field differs. The distinction is the whole reason the snapshot stores a
// manifest and not just a digest:
//   changed — the engine produces a different number for something we pinned.
//             Always a failure; a verified case has regressed.
//   added   — the engine emits something it did not before (a new strategy
//             field). A warning: nothing we asserted has moved, but the case
//             should be re-pinned so the new field starts being covered.
//   removed — something we pinned is gone. A failure; a field that vanishes
//             silently is how coverage rots.
export type SnapshotDiffKind = 'changed' | 'added' | 'removed';
export type SnapshotDiffSeverity = 'failure' | 'warning';

export interface SnapshotFieldDiff {
  path: string;
  kind: SnapshotDiffKind;
  severity: SnapshotDiffSeverity;
  expected: ManifestValue | undefined;
  actual: ManifestValue | undefined;
}

export interface SnapshotRowDiff {
  monthIndex: number;
  claimant: 'client' | 'partner' | null;
  fields: SnapshotFieldDiff[];
}

export type CalcSnapshotImpactCategory =
  | 'summary'
  | 'policy-terms'
  | 'monthly-projection'
  | 'projection-detail'
  | 'snapshot';

/** A reviewer-facing rollup. Raw field differences remain available for technical review. */
export interface CalcSnapshotImpact {
  key: string;
  label: string;
  category: CalcSnapshotImpactCategory;
  severity: SnapshotDiffSeverity;
  differenceCount: number;
  firstMonthIndex: number | null;
  lastMonthIndex: number | null;
  hasSingleValue: boolean;
  expected: ManifestValue | undefined;
  actual: ManifestValue | undefined;
}

export interface CalcSnapshotDiff {
  passed: boolean;
  failures: SnapshotFieldDiff[];
  warnings: SnapshotFieldDiff[];
  // The single most useful number when a case fails: where in the ledger the two
  // runs first parted company.
  firstDivergentMonthIndex: number | null;
  // The divergent month plus its immediate neighbours, so a reviewer can see
  // what led into the break without opening the full ledger.
  rowDiffs: SnapshotRowDiff[];
  totalsDiffs: SnapshotFieldDiff[];
  termsDiffs: SnapshotFieldDiff[];
  manifestDiffs: SnapshotFieldDiff[];
  impactGroups: CalcSnapshotImpact[];
  expectedDigest: string;
  actualDigest: string;
}

const ROW_CONTEXT = 1;

export function diffCalcSnapshots(expected: CalcSnapshot, actual: CalcSnapshot): CalcSnapshotDiff {
  const partnerPolicyTotalsDiffs =
    expected.partnerPolicyTotals === undefined && actual.partnerPolicyTotals == null
      ? []
      : diffManifests(
          buildCanonicalManifest({ partnerPolicyTotals: expected.partnerPolicyTotals }),
          buildCanonicalManifest({ partnerPolicyTotals: actual.partnerPolicyTotals }),
          '',
        );
  const termsDiffs = diffManifests(
    buildCanonicalManifest(expected.terms),
    buildCanonicalManifest(actual.terms),
    'terms',
  );
  const totalsDiffs = [
    ...diffManifests(
      buildCanonicalManifest(expected.ledgerTotals),
      buildCanonicalManifest(actual.ledgerTotals),
      'ledgerTotals',
    ),
    ...diffManifests(
      buildCanonicalManifest(expected.policyTotals),
      buildCanonicalManifest(actual.policyTotals),
      'policyTotals',
    ),
    ...partnerPolicyTotalsDiffs,
  ];
  const manifestDiffs = [
    ...diffManifests(
      buildCanonicalManifest({
        snapshotVersion: expected.snapshotVersion,
        asOfDate: expected.asOfDate,
        fundingSourceId: expected.fundingSourceId,
        strategyId: expected.strategyId,
      }),
      buildCanonicalManifest({
        snapshotVersion: actual.snapshotVersion,
        asOfDate: actual.asOfDate,
        fundingSourceId: actual.fundingSourceId,
        strategyId: actual.strategyId,
      }),
      'snapshot',
    ),
    ...diffManifests(expected.manifest, actual.manifest, 'projection'),
  ];

  const { firstDivergentMonthIndex, rowDiffs } = diffRows(expected.rows, actual.rows);

  const all = [
    ...termsDiffs,
    ...totalsDiffs,
    ...manifestDiffs,
    ...rowDiffs.flatMap(row => row.fields),
  ];

  const failures = all.filter(diff => diff.severity === 'failure');
  const partialDiff = { totalsDiffs, termsDiffs, manifestDiffs, rowDiffs };

  return {
    passed: failures.length === 0,
    failures,
    warnings: all.filter(diff => diff.severity === 'warning'),
    firstDivergentMonthIndex,
    rowDiffs,
    totalsDiffs,
    termsDiffs,
    manifestDiffs,
    impactGroups: summarizeCalcSnapshotDiff(partialDiff),
    expectedDigest: expected.digest,
    actualDigest: actual.digest,
  };
}

export function diffManifests(
  expected: CanonicalManifest,
  actual: CanonicalManifest,
  pathPrefix: string,
): SnapshotFieldDiff[] {
  const diffs: SnapshotFieldDiff[] = [];
  const paths = new Set([...Object.keys(expected), ...Object.keys(actual)]);

  [...paths].sort().forEach(path => {
    const inExpected = path in expected;
    const inActual = path in actual;
    const qualified = pathPrefix ? `${pathPrefix}.${path}` : path;

    // Omitted and explicit null are equivalent for optional calculation fields.
    if ((!inExpected && actual[path] === null) || (!inActual && expected[path] === null)) {
      return;
    }

    if (inExpected && !inActual) {
      diffs.push({
        path: qualified,
        kind: 'removed',
        severity: 'failure',
        expected: expected[path],
        actual: undefined,
      });
      return;
    }
    if (!inExpected && inActual) {
      diffs.push({
        path: qualified,
        kind: 'added',
        severity: 'warning',
        expected: undefined,
        actual: actual[path],
      });
      return;
    }
    if (!valuesMatch(expected[path], actual[path], path)) {
      diffs.push({
        path: qualified,
        kind: 'changed',
        severity: 'failure',
        expected: expected[path],
        actual: actual[path],
      });
    }
  });

  return diffs;
}

export function summarizeCalcSnapshotDiff(
  diff: Pick<CalcSnapshotDiff, 'totalsDiffs' | 'termsDiffs' | 'manifestDiffs' | 'rowDiffs'>,
): CalcSnapshotImpact[] {
  const rawDiffs = [
    ...diff.totalsDiffs,
    ...diff.termsDiffs,
    ...diff.rowDiffs.flatMap(row => row.fields),
    ...diff.manifestDiffs,
  ];
  const uniqueDiffs = new Map<string, SnapshotFieldDiff>();
  rawDiffs.forEach(field => {
    uniqueDiffs.set(`${field.path}:${field.kind}:${field.severity}`, field);
  });

  const groups = new Map<
    string,
    CalcSnapshotImpact & { months: Set<number>; fields: SnapshotFieldDiff[] }
  >();

  uniqueDiffs.forEach(field => {
    const category = impactCategory(field.path);
    const fieldName = impactFieldName(field.path);
    const key = `${field.severity}:${category}:${fieldName}`;
    const monthIndex = extractMonthIndex(field.path);
    const current = groups.get(key) ?? {
      key,
      label: impactLabel(fieldName),
      category,
      severity: field.severity,
      differenceCount: 0,
      firstMonthIndex: null,
      lastMonthIndex: null,
      hasSingleValue: false,
      expected: undefined,
      actual: undefined,
      months: new Set<number>(),
      fields: [],
    };

    current.fields.push(field);
    if (monthIndex !== null) current.months.add(monthIndex);
    groups.set(key, current);
  });

  const categoryOrder: Record<CalcSnapshotImpactCategory, number> = {
    'summary': 0,
    'policy-terms': 1,
    'monthly-projection': 2,
    'projection-detail': 3,
    'snapshot': 4,
  };

  return [...groups.values()]
    .map(group => {
      const months = [...group.months].sort((left, right) => left - right);
      const [onlyField] = group.fields;
      return {
        key: group.key,
        label: group.label,
        category: group.category,
        severity: group.severity,
        differenceCount: group.fields.length,
        firstMonthIndex: months.at(0) ?? null,
        lastMonthIndex: months.at(-1) ?? null,
        hasSingleValue: group.fields.length === 1,
        expected: group.fields.length === 1 ? onlyField.expected : undefined,
        actual: group.fields.length === 1 ? onlyField.actual : undefined,
      };
    })
    .sort((left, right) => {
      if (left.severity !== right.severity) return left.severity === 'failure' ? -1 : 1;
      const categoryDifference = categoryOrder[left.category] - categoryOrder[right.category];
      if (categoryDifference) return categoryDifference;
      return left.label.localeCompare(right.label);
    });
}

function impactCategory(path: string): CalcSnapshotImpactCategory {
  if (/^(ledgerTotals|policyTotals|partnerPolicyTotals)\./.test(path)) return 'summary';
  if (path.startsWith('terms.')) return 'policy-terms';
  if (/(?:projection|rows).*\[\d+\]/.test(path)) return 'monthly-projection';
  if (path.startsWith('snapshot.')) return 'snapshot';
  return 'projection-detail';
}

function impactFieldName(path: string): string {
  const leaf = path.split('.').at(-1) ?? path;
  return leaf.replace(/\[\d+\]/g, '') || 'projectionMonth';
}

function extractMonthIndex(path: string): number | null {
  const match = path.match(/(?:months|rows)\[(\d+)\]/);
  return match ? Number(match[1]) : null;
}

const IMPACT_LABELS: Record<string, string> = {
  appliedMonthlyBenefitCap: 'Monthly benefit limit',
  baseBenefitPool: 'Base benefit balance',
  closingBaseBenefitPool: 'Remaining base benefit',
  closingDeathBenefit: 'Remaining death benefit',
  closingExtensionOfBenefitsPool: 'Remaining extension of benefits',
  closingLtcPool: 'Remaining LTC benefit',
  coverageEligibleDays: 'Eligible coverage days',
  effectiveCareCost: 'Projected care cost',
  epSatisfied: 'Waiting-period eligibility',
  extensionOfBenefitsPool: 'Extension-of-benefits balance',
  indemnityPayout: 'Indemnity payout',
  ineligibleCost: 'Ineligible care cost',
  ltcCoveragePaid: 'LTC policy reimbursement',
  ltcPayoutToCost: 'LTC payout applied to care costs',
  policyCoverageOfRemainingLtcCosts: 'Policy coverage of remaining LTC costs',
  policyProjectedCompoundAnnualGrowthRate: 'Policy projected annual growth rate',
  policyProjectedInternalRateOfReturn: 'Policy projected internal rate of return',
  policyProjectedReturnOnInvestment: 'Policy projected return on investment',
  policyTotalCost: 'Policy total cost',
  policyTotalLTCBenefit: 'Policy total LTC benefit',
  policyTotalValue: 'Policy total value',
  projectedCostCoverage: 'Projected cost coverage',
  projectionMonth: 'Projection month',
  roomAndBoardCovered: 'Room and board covered',
  roomAndBoardCost: 'Room and board cost',
  totalOutOfPocketCost: 'Total out-of-pocket cost',
  totalPayout: 'Total payout',
  totalProjectedCareCost: 'Total projected care cost',
};

function impactLabel(fieldName: string): string {
  const knownLabel = IMPACT_LABELS[fieldName];
  if (knownLabel) return knownLabel;

  return fieldName
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/^./, character => character.toUpperCase())
    .replace(/\bltc\b/g, 'LTC')
    .replace(/\badl\b/g, 'ADL')
    .replace(/\birr\b/g, 'IRR');
}

function diffRows(
  expected: LedgerSnapshotRow[],
  actual: LedgerSnapshotRow[],
): { firstDivergentMonthIndex: number | null; rowDiffs: SnapshotRowDiff[] } {
  const rowDiffs: SnapshotRowDiff[] = [];
  const length = Math.max(expected.length, actual.length);
  let firstDivergentIndex: number | null = null;

  for (let index = 0; index < length; index++) {
    const expectedRow = expected[index];
    const actualRow = actual[index];

    // A ledger that changed length is a real regression, not a row-level field
    // change — report the missing side as one diff rather than a wall of them.
    if (!expectedRow || !actualRow) {
      rowDiffs.push({
        monthIndex: (expectedRow ?? actualRow).monthIndex,
        claimant: (expectedRow ?? actualRow).claimant,
        fields: [
          {
            path: `rows[${index}]`,
            kind: expectedRow ? 'removed' : 'added',
            severity: 'failure',
            expected: expectedRow ? 'present' : undefined,
            actual: actualRow ? 'present' : undefined,
          },
        ],
      });
      firstDivergentIndex ??= index;
      continue;
    }

    const fields = diffManifests(
      buildCanonicalManifest(expectedRow),
      buildCanonicalManifest(actualRow),
      `rows[${index}]`,
    );
    if (fields.length) {
      firstDivergentIndex ??= index;
      rowDiffs.push({ monthIndex: expectedRow.monthIndex, claimant: expectedRow.claimant, fields });
    }
  }

  if (firstDivergentIndex === null) {
    return { firstDivergentMonthIndex: null, rowDiffs: [] };
  }

  // Only the neighbourhood of the break is useful; a strategy change that moves
  // every row would otherwise dump the whole ledger into the diff.
  const from = Math.max(0, firstDivergentIndex - ROW_CONTEXT);
  const to = firstDivergentIndex + ROW_CONTEXT;
  const windowed = rowDiffs.filter(row => {
    const index = expected.findIndex(candidate => candidate.monthIndex === row.monthIndex);
    return index >= from && index <= to;
  });

  return {
    firstDivergentMonthIndex: expected[firstDivergentIndex]?.monthIndex ?? firstDivergentIndex,
    rowDiffs: windowed.length ? windowed : rowDiffs.slice(0, 1 + ROW_CONTEXT * 2),
  };
}

// Human-readable summary for a run result / CI failure message.
export function formatCalcSnapshotDiff(diff: CalcSnapshotDiff): string {
  if (diff.passed && !diff.warnings.length) {
    return 'Snapshot matches.';
  }

  const lines: string[] = [];
  lines.push(
    diff.passed
      ? `Snapshot matches, with ${diff.warnings.length} new field(s).`
      : `Snapshot FAILED: ${diff.failures.length} changed or missing value(s).`,
  );

  if (diff.firstDivergentMonthIndex !== null) {
    lines.push(`First divergent month: ${diff.firstDivergentMonthIndex}`);
  }
  if (diff.expectedDigest !== diff.actualDigest) {
    lines.push(`Digest: ${diff.expectedDigest} -> ${diff.actualDigest}`);
  }

  const describe = (heading: string, entries: SnapshotFieldDiff[]) => {
    if (!entries.length) {
      return;
    }
    lines.push(`\n${heading}:`);
    entries.slice(0, 25).forEach(entry => {
      lines.push(`  ${entry.path}: ${format(entry.expected)} -> ${format(entry.actual)}`);
    });
    if (entries.length > 25) {
      lines.push(`  ...and ${entries.length - 25} more`);
    }
  };

  describe('Totals', diff.totalsDiffs.filter(isFailure));
  describe('Terms', diff.termsDiffs.filter(isFailure));
  describe('Manifest', diff.manifestDiffs.filter(isFailure));
  describe(
    `Rows (around month ${diff.firstDivergentMonthIndex})`,
    diff.rowDiffs.flatMap(row => row.fields).filter(isFailure),
  );
  describe('New fields (warnings)', diff.warnings);

  return lines.join('\n');
}

function isFailure(diff: SnapshotFieldDiff): boolean {
  return diff.severity === 'failure';
}

function format(value: ManifestValue | undefined): string {
  return value === undefined ? '(absent)' : JSON.stringify(value);
}
